import type { IndustryType, MarketSegment } from "../types";
import { cn } from "../lib/utils";

/** Color mapping for industry type badges. */
const INDUSTRY_COLORS: Record<string, string> = {
  lumber: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  burial: "bg-purple-600 text-white dark:bg-purple-500 dark:text-white",
  steel: "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
  pool_paint: "bg-blue-600 text-white dark:bg-blue-500 dark:text-white",
  other: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
};

/** Color mapping for market segment badges. */
const MARKET_COLORS: Record<string, string> = {
  dot: "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300",
  commercial: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  residential: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
};

/** Display labels for industry types. */
const INDUSTRY_LABELS: Record<string, string> = {
  lumber: "Lumber",
  burial: "Burial",
  steel: "Steel",
  pool_paint: "Pool Paint",
  other: "Other",
};

/** Display labels for market segments. */
const MARKET_LABELS: Record<string, string> = {
  dot: "DOT",
  commercial: "Commercial",
  residential: "Residential",
};

interface ClassificationBadgesProps {
  industryType: IndustryType | null;
  marketSegment: MarketSegment | null;
  companyGroup: string | null;
  className?: string;
}

export function ClassificationBadges({
  industryType,
  marketSegment,
  companyGroup,
  className,
}: ClassificationBadgesProps) {
  const hasBadges = industryType || marketSegment || companyGroup;

  if (!hasBadges) return null;

  return (
    <div className={cn("flex flex-wrap gap-1.5", className)}>
      {industryType && (
        <Badge
          label={INDUSTRY_LABELS[industryType] ?? industryType}
          colorClass={INDUSTRY_COLORS[industryType] ?? INDUSTRY_COLORS.other}
        />
      )}
      {marketSegment && (
        <Badge
          label={MARKET_LABELS[marketSegment] ?? marketSegment}
          colorClass={MARKET_COLORS[marketSegment] ?? MARKET_COLORS.dot}
        />
      )}
      {companyGroup && (
        <Badge
          label={companyGroup}
          colorClass="bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300"
        />
      )}
    </div>
  );
}

function Badge({ label, colorClass }: { label: string; colorClass: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5",
        "text-xs font-medium leading-tight",
        colorClass,
      )}
    >
      {label}
    </span>
  );
}
